'use client'
import { notFound } from 'next/navigation'
import React, { FC, PropsWithChildren, useState, useCallback, useEffect } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { isRadiusUser } from '~/helpers/auth'

import { useAuthentication } from '~/hooks/useAuthentication'
import useDeviceDetection from '~/hooks/useDeviceDetection'
import { usePrevPathInitializer } from 'hooks/usePrevPathInitializer'

import { AuthenticatedUserContext } from '~/providers/AuthenticatedUserContext'
import withApollo from '~/hoc/withApollo'
import Settings from '~/src/components/Settings'
import useScreenTimeTracking from '~/app/(authenticated)/useScreenTimeTracking'
import useSprig from '~/app/(authenticated)/useSprig'
import AppcuesIntegration from '~/app/(authenticated)/appcues'
import AppcuesPageTracker from '~/app/(authenticated)/AppcuesPageTracker'
import MedalliaIntegration from '~/app/(authenticated)/medallia'
import useSprigTracking from '~/app/(authenticated)/useSprigTracking'
import useAppcuesSetup from '~/app/(authenticated)/useAppcuesSetup'
import useMedalliaSetup from '~/app/(authenticated)/useMedalliaSetup'
import OperatorAuthenticatedShell from '~/app/(authenticated)/OperatorAuthenticatedShell'
import CustomerAuthenticatedShell from '~/app/(authenticated)/CustomerAuthenticatedShell'

import { UserRole } from '~/__generated_types__/globalTypes'

const AuthenticatedLayout: FC<PropsWithChildren> = ({ children }) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  const authentication = useAuthentication()
  const { startSession } = useScreenTimeTracking()
  const device = useDeviceDetection()
  const flag = useFlags()

  usePrevPathInitializer()

  const [user, logout] = authentication

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false)
  }, [])

  const handleOpenSettings = useCallback(() => {
    setIsSettingsOpen(true)
  }, [])

  useSprig(user?.role, user?.email, process.env.NEXT_PUBLIC_SPRIG_ENVIRONMENT_ID)

  useSprigTracking(user)
  useAppcuesSetup(user)
  useMedalliaSetup(user)

  useEffect(() => {
    user && startSession()
  }, [startSession, user])

  if (!user) return null

  const isOperatorOrSysadmin =
    user.role === UserRole.Operator || user.role === UserRole.Sysadmin
  const isCustomer = user.role === UserRole.Customer

  if (!isOperatorOrSysadmin && !isCustomer) notFound()

  const handleSidebarAction = (key: string) => {
    if (key === 'settings') handleOpenSettings()
    if (key === 'logout') logout()
  }

  return (
    <>
      <AppcuesIntegration shouldLoad={user.role === UserRole.Customer} />
      {user.role === UserRole.Customer && <AppcuesPageTracker />}
      <MedalliaIntegration shouldLoad={!!user.customer} />
      <AuthenticatedUserContext.Provider value={authentication}>
        {isOperatorOrSysadmin ? (
          <OperatorAuthenticatedShell
            device={device}
            user={user}
            onAction={handleSidebarAction}
          >
            {children}
          </OperatorAuthenticatedShell>
        ) : (
          <CustomerAuthenticatedShell device={device} user={user}>
            {children}
          </CustomerAuthenticatedShell>
        )}

        {isRadiusUser(user) && isSettingsOpen && (
          <Settings
            showHomePageSetting={flag['showHomePageSetting'] && device.isDesktop}
            onCloseDialog={handleCloseSettings}
          />
        )}
      </AuthenticatedUserContext.Provider>
    </>
  )
}
export default withApollo()(AuthenticatedLayout)
